import React, { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pencil, Trash2, Loader2, X } from "lucide-react";

const inputClass = "w-full rounded-md border border-[hsl(240,6%,88%)] bg-white px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[hsl(245,58%,70%)]";

export function Field({ field, value, onChange }) {
  const common = {
    id: field.key,
    value: value ?? "",
    onChange: (e) => onChange(field.key, field.type === "number" ? Number(e.target.value) : e.target.value),
    placeholder: field.placeholder,
    required: field.required,
    autoFocus: field.autoFocus,
    className: inputClass,
  };

  return (
    <div className={field.fullWidth ? "sm:col-span-2" : ""}>
      <label htmlFor={field.key} className="block text-[12.5px] font-medium mb-1">
        {field.label} {field.required && <span className="text-red-500">*</span>}
      </label>
      {field.type === "textarea" ? (
        <textarea {...common} rows={field.rows || 3} className={`${inputClass} resize-none`} />
      ) : field.type === "select" ? (
        <select {...common}>
          {(field.options || []).map((option) => {
            const item = typeof option === "string" ? { label: option, value: option } : option;
            return <option key={item.value} value={item.value}>{item.label}</option>;
          })}
        </select>
      ) : (
        <input {...common} type={field.type || "text"} min={field.min} max={field.max} step={field.step} />
      )}
      {field.help && <div className="text-[11px] text-[hsl(240,4%,55%)] mt-1">{field.help}</div>}
    </div>
  );
}

export function CrudModal({
  open,
  onClose,
  title,
  description,
  fields,
  initialValues = {},
  onSubmit,
  submitLabel = "Save",
  size = "max-w-lg",
}) {
  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setError("");
      setSaving(false);
    }
  }, [open, initialValues]);

  if (!open) return null;

  const setValue = (key, value) => setValues((current) => ({ ...current, [key]: value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onSubmit(values);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not save changes.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form onSubmit={submit} className={`w-full ${size} max-h-[90vh] overflow-auto rounded-xl bg-white shadow-xl border border-[hsl(240,6%,90%)] animate-in-up`}>
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-[hsl(240,6%,92%)]">
          <div>
            <h3 className="text-[15px] font-semibold">{title}</h3>
            {description && <p className="text-[12px] text-[hsl(240,4%,50%)] mt-1">{description}</p>}
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded hover:bg-[hsl(240,5%,95%)] disabled:opacity-50" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {fields.map((field) => <Field key={field.key} field={field} value={values[field.key]} onChange={setValue} />)}
          {error && <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-[hsl(240,6%,92%)]">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-md px-3 py-1.5 text-[13px] font-medium text-[hsl(240,4%,40%)] hover:bg-[hsl(240,5%,95%)] disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={saving} className="rounded-md brand-bg text-white px-3 py-1.5 text-[13px] font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title = "Delete record?", description, busy = false, confirmLabel = "Delete" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-[hsl(240,6%,90%)]">
        <div className="px-5 py-4">
          <h3 className="text-[15px] font-semibold">{title}</h3>
          <p className="text-[13px] text-[hsl(240,4%,45%)] mt-2">{description || "This action cannot be undone."}</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-[hsl(240,6%,92%)]">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-md px-3 py-1.5 text-[13px] font-medium text-[hsl(240,4%,40%)] hover:bg-[hsl(240,5%,95%)] disabled:opacity-50">Cancel</button>
          <button type="button" onClick={onConfirm} disabled={busy} className="rounded-md bg-red-600 text-white px-3 py-1.5 text-[13px] font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ActionMenu({ onEdit, onDelete, editLabel = "Edit", deleteLabel = "Delete" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={ref} className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((value) => !value)} className="p-1.5 rounded-md text-[hsl(240,4%,45%)] hover:bg-[hsl(240,5%,95%)]" aria-label="More actions">
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-30 min-w-[130px] rounded-md border border-[hsl(240,6%,88%)] bg-white p-1 shadow-lg">
          {onEdit && <button type="button" onClick={() => { setOpen(false); onEdit(); }} className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-[hsl(240,5%,96%)]"><Pencil className="h-3.5 w-3.5" /> {editLabel}</button>}
          {onDelete && <button type="button" onClick={() => { setOpen(false); onDelete(); }} className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12.5px] text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> {deleteLabel}</button>}
        </div>
      )}
    </div>
  );
}
